import React, { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

export const DATA_TYPES = ["Visitor arrivals", "Revenue", "Occupancy rate", "Search demand", "Bookings"];
export const RECORDS_TYPE = "Records";

// Where each canonical metric surfaces across the app (informational — "Aperçu de l'impact").
export const METRIC_IMPACT = {
  "Visitor arrivals": ["Tableau de bord", "Markets", "Opportunities"],
  "Revenue": ["Tableau de bord", "Markets", "Performance", "Opportunities"],
  "Occupancy rate": ["Tableau de bord", "Opportunities"],
  "Search demand": ["Tableau de bord", "Opportunities"],
  "Bookings": ["Tableau de bord", "Performance", "Opportunities"],
};

// --- Client-side schema discovery (fallback when the backend does not return `discovery`) ---
const MONTH_ALIASES = {
  janvier: 1, janv: 1, jan: 1, january: 1, fevrier: 2, "février": 2, fevr: 2, "févr": 2, fev: 2, "fév": 2, february: 2, feb: 2,
  mars: 3, march: 3, mar: 3, avril: 4, avr: 4, april: 4, apr: 4, mai: 5, may: 5, juin: 6, june: 6, jun: 6,
  juillet: 7, juil: 7, july: 7, jul: 7, aout: 8, "août": 8, august: 8, aug: 8, septembre: 9, sept: 9, sep: 9, september: 9,
  octobre: 10, oct: 10, october: 10, novembre: 11, nov: 11, november: 11, decembre: 12, "décembre": 12, dec: 12, "déc": 12, december: 12,
};
const clientParsePeriodHeader = (header) => {
  const h = String(header ?? "").trim().toLowerCase();
  if (!h) return null;
  if (/^(19|20)\d{2}$/.test(h)) return `${h}-01-01`;
  let m = h.match(/^((?:19|20)\d{2})[-/_.](\d{1,2})$/);
  if (m) { const mo = +m[2]; if (mo >= 1 && mo <= 12) return `${m[1]}-${String(mo).padStart(2, "0")}-01`; }
  m = h.match(/^(\d{1,2})[-/_.]((?:19|20)\d{2})$/);
  if (m) { const mo = +m[1]; if (mo >= 1 && mo <= 12) return `${m[2]}-${String(mo).padStart(2, "0")}-01`; }
  const ym = h.match(/(19|20)\d{2}/);
  const q = h.match(/\b[qt]([1-4])\b/);
  if (q && ym) { const mo = (+q[1] - 1) * 3 + 1; return `${ym[0]}-${String(mo).padStart(2, "0")}-01`; }
  if (ym) {
    for (const alias of Object.keys(MONTH_ALIASES).sort((a, b) => b.length - a.length)) {
      if (new RegExp(`(?:^|[^a-zéèêëàâîïôûüç])${alias}`).test(h)) return `${ym[0]}-${String(MONTH_ALIASES[alias]).padStart(2, "0")}-01`;
    }
    return `${ym[0]}-01-01`;
  }
  return null;
};
const clientDetectType = (vals) => {
  const s = vals.map((v) => String(v ?? "").trim()).filter((x) => x !== "").slice(0, 50);
  if (!s.length) return "unknown";
  const frac = (p) => s.filter(p).length / s.length;
  const isNum = (v) => !isNaN(parseFloat(String(v).replace(",", ".").replace(/\s/g, "").replace(/[%€$£]/g, "")));
  if (frac((v) => /^(19|20)\d{2}$/.test(v)) > 0.8) return "year";
  if (frac((v) => /^\d{4}[-/]\d{1,2}([-/]\d{1,2})?$|^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(v)) > 0.7) return "date";
  if (frac((v) => v.endsWith("%")) > 0.6) return "percentage";
  if (frac(isNum) > 0.8) return "number";
  return "text";
};
const DT_SYNONYMS = [
  ["Search demand", ["search demand", "search volume", "searches", "recherche"]],
  ["Occupancy rate", ["occupancy", "occ rate", "taux d'occupation", "occupation"]],
  ["Visitor arrivals", ["visitor arrivals", "arrivals", "visitors", "arrivées", "arrivees", "pax", "passengers", "fréquentation", "frequentation"]],
  ["Bookings", ["booking", "bookings", "reservation", "réservation", "reservations"]],
  ["Revenue", ["revenue", "revenu", "chiffre", "turnover", "sales", "recette", "ventes", "vente", "montant", "spend"]],
];
const clientSuggestDataType = (name) => {
  const n = String(name ?? "").toLowerCase();
  for (const [dt, kws] of DT_SYNONYMS) for (const kw of kws) if (n.includes(kw)) return dt;
  return null;
};
export const buildClientDiscovery = (columns, rows) => {
  const cols = (columns || []).map((c) => {
    const vals = (rows || []).slice(0, 50).map((r) => r?.[c]);
    const nonempty = vals.filter((v) => String(v ?? "").trim() !== "").map((v) => String(v));
    const ttype = clientDetectType(vals);
    let role = "dimension";
    if (ttype === "date" || ttype === "year") role = "period";
    else if (ttype === "number" || ttype === "percentage") role = "metric";
    return { column: c, technical_type: ttype, role, sample: nonempty.slice(0, 3), suggested_data_type: clientSuggestDataType(c) };
  });
  const periodHeaders = (columns || []).filter((c) => clientParsePeriodHeader(c));
  return {
    columns: cols,
    period_candidates: cols.filter((c) => c.role === "period").map((c) => c.column),
    metric_candidates: cols.filter((c) => c.role === "metric").map((c) => c.column),
    dimension_candidates: cols.filter((c) => c.role === "dimension").map((c) => c.column),
    wide_format: periodHeaders.length >= 2,
    period_header_columns: periodHeaders,
  };
};

export const SEMANTIC_CATEGORIES = [
  "Source Market", "Destination", "Travel Period", "Passenger Volume", "Air Capacity",
  "Hotel Capacity", "Occupancy", "ADR", "Revenue", "Visitor Spend", "Length of Stay",
  "Booking Volume", "Campaign Spend", "Leads", "Conversions", "Custom Metric",
];

const STEP_LABELS = ["Upload", "Preview", "Mapping", "Validation", "Import"];

const Stepper = ({ step }) => (
  <div className="mb-6 flex items-center gap-2">
    {STEP_LABELS.map((s, i) => (
      <React.Fragment key={s}>
        <div className="flex items-center gap-1.5">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
              i < step ? "bg-[#3E7256] text-white" : i === step ? "bg-[#1E3D2B] text-white" : "bg-slate-100 text-slate-400"
            }`}
          >
            {i + 1}
          </span>
          <span className={`hidden text-xs font-medium sm:inline ${i === step ? "text-[#1E3D2B]" : "text-slate-400"}`}>{s}</span>
        </div>
        {i < STEP_LABELS.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
      </React.Fragment>
    ))}
  </div>
);

export const ImportWizard = ({ open, onOpenChange, orgId, kind, onDone }) => {
  const accept = kind === "xlsx" ? ".xlsx,.xls" : ".csv";
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [map, setMap] = useState({ date_col: "", value_col: "", category_col: "__none__", data_type: "", source_name: "" });
  const [colCats, setColCats] = useState({});
  const [periodCol, setPeriodCol] = useState("__none__");
  const [dimensionCol, setDimensionCol] = useState("__none__");
  const [metricMap, setMetricMap] = useState({});
  const [mode, setMode] = useState("adaptive"); // 'adaptive' | 'pivot'
  const [pivot, setPivot] = useState({ data_type: "", dimension_col: "__none__", period_columns: [] });
  const [templateInfo, setTemplateInfo] = useState(null);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(0); setFile(null); setBusy(false); setDraft(null);
    setMap({ date_col: "", value_col: "", category_col: "__none__", data_type: "", source_name: "" });
    setColCats({});
    setPeriodCol("__none__"); setDimensionCol("__none__"); setMetricMap({});
    setMode("adaptive"); setPivot({ data_type: "", dimension_col: "__none__", period_columns: [] });
    setTemplateInfo(null);
    setValidation(null); setResult(null);
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const applyTemplate = (tpl, cols) => {
    if (!tpl || !tpl.found) return;
    const present = new Set(cols || []);
    if (tpl.column_categories) setColCats((c) => ({ ...c, ...tpl.column_categories }));
    if (tpl.mode === "adaptive" && Array.isArray(tpl.mappings)) {
      const first = tpl.mappings[0] || {};
      const pc = first.date_col && present.has(first.date_col) ? first.date_col : "__none__";
      const dc = first.category_col && present.has(first.category_col) ? first.category_col : "__none__";
      const mm = {};
      tpl.mappings.forEach((m) => { if (present.has(m.value_col)) mm[m.value_col] = m.data_type; });
      setMode("adaptive"); setPeriodCol(pc); setDimensionCol(dc);
      setMetricMap((prev) => ({ ...prev, ...mm }));
    } else if (tpl.mode === "pivot" && tpl.pivot) {
      const pcs = (tpl.pivot.period_columns || []).filter((c) => present.has(c));
      setMode("pivot");
      setPivot({
        data_type: tpl.pivot.data_type || "",
        dimension_col: tpl.pivot.dimension_col && present.has(tpl.pivot.dimension_col) ? tpl.pivot.dimension_col : "__none__",
        period_columns: pcs,
      });
    } else if (tpl.mode === "legacy" && tpl.legacy) {
      const l = tpl.legacy;
      setMode("adaptive");
      setPeriodCol(l.date_col && present.has(l.date_col) ? l.date_col : "__none__");
      setDimensionCol(l.category_col && present.has(l.category_col) ? l.category_col : "__none__");
      if (l.value_col && present.has(l.value_col) && l.data_type) {
        setMetricMap((prev) => ({ ...prev, [l.value_col]: l.data_type }));
      }
    }
    setTemplateInfo(tpl);
  };

  const loadTemplate = async (sourceName, cols) => {
    const name = (sourceName || "").trim();
    if (!name) return;
    try {
      const { data } = await api.get(`/organizations/${orgId}/imports/mapping-template`, { params: { source_name: name, columns: (cols || []).join(",") } });
      if (data?.found) applyTemplate(data, cols);
      else setTemplateInfo(null);
    } catch (e) { /* non-blocking */ }
  };

  const doParse = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/organizations/${orgId}/imports/parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft(data);
      setMap((m) => ({ ...m, source_name: data.filename }));
      setColCats(data.suggestions || {});
      const disc = data.discovery || buildClientDiscovery(data.columns, data.preview);
      const pc = disc.period_candidates?.[0] || "__none__";
      const dc = disc.dimension_candidates?.[0] || "__none__";
      const mm = {};
      (disc.columns || []).forEach((col) => { mm[col.column] = col.suggested_data_type || "__ignore__"; });
      if (pc !== "__none__") mm[pc] = "__ignore__";
      if (dc !== "__none__") mm[dc] = "__ignore__";
      setPeriodCol(pc); setDimensionCol(dc); setMetricMap(mm);
      if (disc.wide_format) {
        setMode("pivot");
        setPivot({ data_type: "", dimension_col: disc.dimension_candidates?.[0] || "__none__",
          period_columns: disc.period_header_columns || [] });
      } else {
        setMode("adaptive");
      }
      await loadTemplate(data.filename, data.columns);
      setStep(1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const discovery = draft ? (draft.discovery || buildClientDiscovery(draft.columns, draft.preview)) : null;
  const adaptive = !!discovery;
  const allCols = draft?.columns || [];
  const colMeta = {};
  (discovery?.columns || []).forEach((c) => { colMeta[c.column] = c; });
  const metricColumns = allCols.filter((c) => c !== periodCol && c !== dimensionCol);
  const adaptiveMappings = () => {
    const metrics = metricColumns
      .filter((c) => metricMap[c] && metricMap[c] !== "__ignore__")
      .map((c) => ({
        date_col: periodCol === "__none__" ? "" : periodCol,
        value_col: c,
        category_col: dimensionCol === "__none__" ? null : dimensionCol,
        data_type: metricMap[c],
      }));
    if (metrics.length) return metrics;
    // Categorical-only fallback: a dimension is chosen but no numeric metric was classified.
    if (dimensionCol !== "__none__") {
      return [{ date_col: periodCol === "__none__" ? "" : periodCol, value_col: "",
        category_col: dimensionCol, data_type: RECORDS_TYPE }];
    }
    return [];
  };

  const payload = () => {
    if (mode === "pivot") {
      return {
        source_name: map.source_name,
        column_categories: colCats,
        pivot: {
          data_type: pivot.data_type,
          dimension_col: pivot.dimension_col === "__none__" ? null : pivot.dimension_col,
          period_columns: pivot.period_columns,
        },
      };
    }
    return { source_name: map.source_name, column_categories: colCats, mappings: adaptiveMappings() };
  };

  const doValidate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/imports/${draft.import_id}/validate`, payload());
      setValidation(data);
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/imports/${draft.import_id}/commit`, payload());
      if (!data.row_count) {
        toast.error("Import impossible : aucune ligne exploitable n'a été importée. Vérifiez le mapping des colonnes.");
        return;
      }
      setResult(data);
      setStep(4);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const togglePivotPeriod = (col) => setPivot((p) => ({
    ...p,
    period_columns: p.period_columns.includes(col)
      ? p.period_columns.filter((c) => c !== col)
      : [...p.period_columns, col],
  }));
  const mappingReady = mode === "pivot"
    ? (!!pivot.data_type && pivot.period_columns.length >= 1)
    : adaptiveMappings().length >= 1;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle>Importer un fichier {kind.toUpperCase()}</DialogTitle>
          <DialogDescription>Sans code — suivez les étapes ci-dessous.</DialogDescription>
        </DialogHeader>

        {step < 4 && <Stepper step={step} />}

        {/* Step 0: Upload */}
        {step === 0 && (
          <div data-testid="wizard-step-upload" className="space-y-4">
            <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center transition-colors hover:border-[#3E7256]">
              <UploadCloud size={32} className="text-[#3E7256]" />
              <span className="text-sm font-medium text-slate-700">
                {file ? file.name : `Choisir un fichier ${kind.toUpperCase()}`}
              </span>
              <span className="text-xs text-slate-400">{accept}</span>
              <input type="file" accept={accept} className="hidden" data-testid="wizard-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <div className="flex justify-end">
              <button disabled={!file || busy} onClick={doParse} data-testid="wizard-parse-btn"
                className="flex h-10 items-center gap-2 rounded-lg bg-[#1E3D2B] px-5 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-50">
                {busy ? "Analyse…" : <>Analyser <ArrowRight size={15} /></>}
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Preview */}
        {step === 1 && draft && (
          <div data-testid="wizard-step-preview" className="space-y-4">
            <p className="text-sm text-slate-500">{draft.row_count} lignes détectées · aperçu des 10 premières</p>
            <div className="max-h-64 overflow-auto rounded-lg border border-slate-200">
              <SelectableTable rows={draft.preview} rowId={(r, i) => i} filename="apercu-import.csv"
                ariaLabel="Aperçu d'import — sélection et défilement"
                csvColumns={draft.columns.map((c) => ({ label: c, get: (r) => r[c] }))}>
                <table className="w-full text-xs min-w-[720px]">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    <th className="px-3 py-2"><SelectAllHeaderCheckbox /></th>
                    {draft.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold text-slate-600">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {draft.preview.map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5"><RowCheckbox id={i} label={`Sélectionner la ligne ${i + 1}`} /></td>
                      {draft.columns.map((c) => <td key={c} className="px-3 py-1.5 text-slate-600">{row[c]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              </SelectableTable>
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(0)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1E3D2B]"><ArrowLeft size={15}/> Retour</button>
              <button onClick={() => setStep(2)} data-testid="wizard-to-mapping" className="flex h-10 items-center gap-2 rounded-lg bg-[#1E3D2B] px-5 text-sm font-semibold text-white hover:bg-[#2C5540]">Continuer <ArrowRight size={15} /></button>
            </div>
          </div>
        )}

        {/* Step 2: Mapping */}
        {step === 2 && draft && (
          <div data-testid="wizard-step-mapping" className="space-y-4">
            {adaptive && (
              <div className="space-y-4" data-testid="adaptive-mapping">
                {templateInfo?.found && (
                  <div className="rounded-lg border border-[#3E7256]/30 bg-[#ECF3EC]/60 px-3.5 py-2 text-xs text-[#1E3D2B]" data-testid="template-badge">
                    Rôles pré-remplis depuis un import précédent portant le même nom de source. Vous pouvez les modifier.
                  </div>
                )}
                <div className="rounded-lg border border-[#3E7256]/30 bg-[#ECF3EC]/50 px-3.5 py-2.5 text-xs text-[#1E3D2B]">
                  Mode adaptatif — structure détectée automatiquement. Confirmez les rôles ci-dessous.
                </div>
                {discovery?.wide_format && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="mode-switch">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Format :</span>
                    <button onClick={() => setMode("pivot")} data-testid="mode-pivot"
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${mode === "pivot" ? "bg-[#1E3D2B] text-white" : "border border-slate-200 text-slate-500 hover:border-[#3E7256]"}`}>Large (périodes en colonnes)</button>
                    <button onClick={() => setMode("adaptive")} data-testid="mode-standard"
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${mode === "adaptive" ? "bg-[#1E3D2B] text-white" : "border border-slate-200 text-slate-500 hover:border-[#3E7256]"}`}>Standard</button>
                  </div>
                )}

                {mode === "pivot" ? (
                  <div className="space-y-4" data-testid="pivot-mapping">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-700">
                      Format large : chaque colonne-période sera « dépliée » en points datés d'une même métrique.
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type de données *</label>
                        <Select value={pivot.data_type} onValueChange={(v) => setPivot({ ...pivot, data_type: v })}>
                          <SelectTrigger className="h-11" data-testid="pivot-data-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
                          <SelectContent>{DATA_TYPES.map((d) => <SelectItem key={d} value={d} data-testid={`pivot-dt-${d}`}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <MapSelect label="Colonne dimension / catégorie (optionnel)" value={pivot.dimension_col} onChange={(v) => setPivot({ ...pivot, dimension_col: v })} options={allCols} testid="pivot-dimension" allowNone />
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pivot-periods">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Colonnes-périodes à déplier</p>
                      <p className="mb-3 text-xs text-slate-400">Cliquez pour inclure/exclure une colonne.</p>
                      <div className="flex flex-wrap gap-2">
                        {allCols.map((col) => {
                          const on = pivot.period_columns.includes(col);
                          return (
                            <button key={col} onClick={() => togglePivotPeriod(col)} data-testid={`pivot-period-${col}`}
                              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-[#3E7256] bg-[#ECF3EC] text-[#1E3D2B]" : "border-slate-200 text-slate-500 hover:border-[#CBD5E1]"}`}>{col}</button>
                          );
                        })}
                      </div>
                    </div>
                    {pivot.data_type && <ImpactNote type={pivot.data_type} />}
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nom de la source</label>
                      <input value={map.source_name} onChange={(e) => setMap({ ...map, source_name: e.target.value })} onBlur={() => loadTemplate(map.source_name, allCols)} data-testid="pivot-source-name"
                        className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#3E7256] focus:ring-2 focus:ring-[#3E7256]/15" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <MapSelect label="Colonne temporelle (période)" value={periodCol} onChange={setPeriodCol} options={allCols} testid="adaptive-period" allowNone />
                      <MapSelect label="Colonne dimension / catégorie (optionnel)" value={dimensionCol} onChange={setDimensionCol} options={allCols} testid="adaptive-dimension" allowNone />
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="adaptive-metrics">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Métriques à importer</p>
                      <p className="mb-3 text-xs text-slate-400">Associez chaque colonne à un type de données, ou laissez « Ignorer » pour la conserver sans l'importer.</p>
                      <div className="max-h-56 space-y-2 overflow-auto">
                        {metricColumns.map((col) => (
                          <div key={col} className="grid grid-cols-[1fr_1.2fr] items-start gap-3">
                            <div className="min-w-0 pt-1.5">
                              <p className="truncate text-sm font-medium text-slate-700" title={col}>{col}</p>
                              {colMeta[col] && (
                                <p className="truncate text-[11px] text-slate-400">
                                  {colMeta[col].technical_type}{(colMeta[col].sample || []).length ? ` · ex: ${colMeta[col].sample.join(", ")}` : ""}
                                </p>
                              )}
                            </div>
                            <div className="space-y-1">
                              <Select value={metricMap[col] || "__ignore__"} onValueChange={(v) => setMetricMap({ ...metricMap, [col]: v })}>
                                <SelectTrigger className="h-9 text-xs" data-testid={`metric-${col}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__ignore__">Ignorer</SelectItem>
                                  {DATA_TYPES.map((d) => <SelectItem key={d} value={d} data-testid={`metric-dt-${col}-${d}`}>{d}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              {METRIC_IMPACT[metricMap[col]] && (
                                <p className="text-[11px] text-[#3E7256]" data-testid={`impact-${col}`}>Alimente : {METRIC_IMPACT[metricMap[col]].join(" · ")}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nom de la source</label>
                      <input value={map.source_name} onChange={(e) => setMap({ ...map, source_name: e.target.value })} onBlur={() => loadTemplate(map.source_name, allCols)} data-testid="adaptive-source-name"
                        className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#3E7256] focus:ring-2 focus:ring-[#3E7256]/15" />
                    </div>
                    {!mappingReady && (
                      <p className="text-xs text-slate-400" data-testid="adaptive-hint">Classez au moins une colonne à un type de données pour continuer.</p>
                    )}
                    {periodCol === "__none__" && mappingReady && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-xs text-slate-500" data-testid="no-period-note">
                        Aucune dimension temporelle détectée. Les données seront importées normalement et resteront disponibles pour les analyses non chronologiques (par pays, catégorie, segment…).
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1E3D2B]"><ArrowLeft size={15}/> Retour</button>
              <button disabled={!mappingReady || busy} onClick={doValidate} data-testid="wizard-validate-btn"
                className="flex h-10 items-center gap-2 rounded-lg bg-[#1E3D2B] px-5 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-50">Valider <ArrowRight size={15} /></button>
            </div>
          </div>
        )}

        {/* Step 3: Validation */}
        {step === 3 && validation && (
          <div data-testid="wizard-step-validation" className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total" value={validation.total} />
              <Stat label="Valides" value={validation.valid} good />
              <Stat label="Ignorées" value={validation.invalid} warn={validation.invalid > 0} />
            </div>
            {validation.series && validation.series.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="validation-series">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Métriques</p>
                <ul className="space-y-1 text-xs text-slate-600">
                  {validation.series.map((s, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="truncate">{s.value_col} → <span className="font-medium text-[#1E3D2B]">{s.data_type}</span></span>
                      <span className={s.status === "ok" ? "text-emerald-700" : "text-amber-700"}>
                        {s.status === "unknown_metric" ? "type non canonique" : s.status === "no_valid_rows" ? "aucune ligne temporelle" : `${s.valid} valides`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {validation.invalid > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="validation-errors">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-700"><AlertTriangle size={13}/> Lignes ignorées</p>
                <ul className="max-h-28 space-y-0.5 overflow-auto text-xs text-amber-700">
                  {validation.invalid_sample.map((e, i) => <li key={i}>Ligne {e.row}{e.metric ? ` (${e.metric})` : ""}: {e.reason}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1E3D2B]"><ArrowLeft size={15}/> Retour</button>
              <button disabled={busy || validation.valid === 0} onClick={doCommit} data-testid="wizard-import-btn"
                className="flex h-10 items-center gap-2 rounded-lg bg-[#3E7256] px-5 text-sm font-semibold text-white hover:bg-[#2F5A43] disabled:opacity-50">
                {busy ? "Import…" : `Importer ${validation.valid} lignes`}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && result && (
          <div data-testid="wizard-step-confirmation" className="space-y-5 py-4 text-center">
            <CheckCircle2 size={48} className="mx-auto text-[#3E7256]" />
            <div>
              <h3 className="font-heading text-lg font-bold text-[#1E3D2B]">Import réussi</h3>
              <p className="mt-1 text-sm text-slate-500">{result.row_count} lignes importées{result.skipped ? ` · ${result.skipped} ignorées` : ""}.</p>
            </div>
            <div className="mx-auto max-w-sm space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-sm">
              {result.adaptive ? (
                <>
                  {(result.series || []).map((s, i) => (
                    <Row key={i} k={s.data_type} v={s.status === "normalized" ? `${s.mapped} lignes` : "conservé en brut"} />
                  ))}
                  <Row k="Source" v={result.source_name} />
                  <Row k="Période couverte" v={result.period_start ? `${result.period_start} → ${result.period_end}` : "—"} />
                </>
              ) : (
                <>
                  <Row k="Type de données" v={result.data_type} />
                  <Row k="Source" v={result.source_name} />
                  <Row k="Période couverte" v={`${result.period_start} → ${result.period_end}`} />
                </>
              )}
            </div>
            {result.warnings && result.warnings.length > 0 && (
              <div className="mx-auto max-w-sm space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-left text-xs text-amber-700" data-testid="import-warnings">
                {result.warnings.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}
            <button onClick={() => close(false)} data-testid="wizard-close-btn"
              className="h-10 rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540]">Terminer</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MapSelect = ({ label, value, onChange, options, testid, allowNone }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid={testid}><SelectValue placeholder="Choisir" /></SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value="__none__">— Aucune —</SelectItem>}
        {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const ImpactNote = ({ type }) => {
  const targets = METRIC_IMPACT[type];
  if (!targets) return null;
  return (
    <div className="rounded-lg border border-[#3E7256]/20 bg-[#ECF3EC]/40 px-3.5 py-2.5 text-xs text-[#1E3D2B]" data-testid="impact-note">
      <span className="font-semibold">Aperçu de l'impact :</span> « {type} » alimentera {targets.join(" · ")}.
    </div>
  );
};

const Stat = ({ label, value, good, warn }) => (  <div className={`rounded-lg border p-4 text-center ${good ? "border-emerald-200 bg-emerald-50" : warn ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
    <div className={`font-heading text-2xl font-bold ${good ? "text-emerald-700" : warn ? "text-amber-700" : "text-[#1E3D2B]"}`}>{value}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><span className="text-slate-500">{k}</span><span className="font-medium text-[#1E3D2B]">{v}</span></div>
);

export const ApiConnect = ({ open, onOpenChange, orgId, onDone }) => {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (open) {
      api.get("/api-providers").then(({ data }) => setProviders(data.providers)).catch(() => {});
    }
  }, [open]);

  const reset = () => { setProvider(""); setResult(null); setBusy(false); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/imports/api-connect`, { provider });
      setResult(data);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="api-connect">
        <DialogHeader>
          <DialogTitle>API Connection</DialogTitle>
          <DialogDescription>Connectez une source externe (démo — données simulées).</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-5 py-4 text-center" data-testid="api-confirmation">
            <CheckCircle2 size={48} className="mx-auto text-[#3E7256]" />
            <h3 className="font-heading text-lg font-bold text-[#1E3D2B]">{result.row_count} lignes synchronisées</h3>
            <p className="text-sm text-slate-500">{result.source_name} · {result.data_type}</p>
            <p className="text-xs text-slate-400">Période : {result.period_start} → {result.period_end}</p>
            <button onClick={() => close(false)} className="h-10 rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540]">Terminer</button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-700">
              Connecteur de démonstration : les données sont simulées, aucune authentification externe réelle n'est effectuée.
            </div>
            <div className="space-y-2" data-testid="api-provider-list">
              {providers.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProvider(p.id)}
                  data-testid={`api-provider-${p.id}`}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-left transition-all ${
                    provider === p.id ? "border-[#3E7256] bg-[#ECF3EC]" : "border-slate-200 hover:border-[#CBD5E1]"
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold text-[#1E3D2B]">{p.label}</p>
                    <p className="text-xs text-slate-400">{p.data_type}</p>
                  </div>
                  {provider === p.id && <CheckCircle2 size={18} className="text-[#3E7256]" />}
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button disabled={!provider || busy} onClick={connect} data-testid="api-connect-btn"
                className="flex h-10 items-center gap-2 rounded-lg bg-[#3E7256] px-5 text-sm font-semibold text-white hover:bg-[#2F5A43] disabled:opacity-50">
                {busy ? "Connexion…" : "Connecter"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ManualImport = ({ open, onOpenChange, orgId, onDone }) => {
  const [dataType, setDataType] = useState("");
  const [sourceName, setSourceName] = useState("Saisie manuelle");
  const [rows, setRows] = useState([{ date: "", value: "", category: "" }]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setDataType(""); setSourceName("Saisie manuelle"); setRows([{ date: "", value: "", category: "" }]); setResult(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };
  const update = (i, k, val) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: val } : r));
  const addRow = () => setRows([...rows, { date: "", value: "", category: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      const clean = rows.filter((r) => r.date && r.value);
      const { data } = await api.post(`/organizations/${orgId}/imports/manual`, { data_type: dataType, source_name: sourceName, rows: clean });
      setResult(data);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const ready = dataType && rows.some((r) => r.date && r.value);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="manual-import">
        <DialogHeader>
          <DialogTitle>Saisie manuelle des données</DialogTitle>
          <DialogDescription>Ajoutez vos données ligne par ligne.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-5 py-4 text-center" data-testid="manual-confirmation">
            <CheckCircle2 size={48} className="mx-auto text-[#3E7256]" />
            <h3 className="font-heading text-lg font-bold text-[#1E3D2B]">{result.row_count} lignes importées</h3>
            <p className="text-sm text-slate-500">Période : {result.period_start} → {result.period_end}</p>
            <button onClick={() => close(false)} className="h-10 rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540]">Terminer</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type de données *</label>
                <Select value={dataType} onValueChange={setDataType}>
                  <SelectTrigger className="h-11" data-testid="manual-data-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{DATA_TYPES.map((d) => <SelectItem key={d} value={d} data-testid={`manual-dt-${d}`}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nom de la source</label>
                <input value={sourceName} onChange={(e) => setSourceName(e.target.value)} data-testid="manual-source-name"
                  className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#3E7256]" />
              </div>
            </div>
            <div className="space-y-2" data-testid="manual-rows">
              <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                <span>Date (AAAA-MM-JJ)</span><span>Valeur</span><span>Catégorie</span><span></span>
              </div>
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <input value={r.date} onChange={(e) => update(i, "date", e.target.value)} placeholder="2025-01-15" data-testid={`manual-date-${i}`}
                    className="h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#3E7256]" />
                  <input value={r.value} onChange={(e) => update(i, "value", e.target.value)} placeholder="1200" data-testid={`manual-value-${i}`}
                    className="h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#3E7256]" />
                  <input value={r.category} onChange={(e) => update(i, "category", e.target.value)} placeholder="Marché" data-testid={`manual-cat-${i}`}
                    className="h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#3E7256]" />
                  <button onClick={() => removeRow(i)} className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-rose-600"><Trash2 size={15} /></button>
                </div>
              ))}
              <button onClick={addRow} data-testid="manual-add-row" className="flex items-center gap-1.5 text-sm font-medium text-[#3E7256] hover:underline"><Plus size={15} /> Ajouter une ligne</button>
            </div>
            <div className="flex justify-end">
              <button disabled={!ready || busy} onClick={submit} data-testid="manual-submit"
                className="flex h-10 items-center gap-2 rounded-lg bg-[#3E7256] px-5 text-sm font-semibold text-white hover:bg-[#2F5A43] disabled:opacity-50">
                {busy ? "Import…" : "Importer"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
